import { Box } from "@mui/material";
import dayjs from "dayjs";
import { IEmployeeInfo } from "../routes/paid-personal-leave-request/PaidPersonalLeaveRequestData";
import { ProgressBarSteps } from "../routes/paid-personal-leave-request/PaidPersonalLeaveRequestModels";
import { DATE_FORMAT, isValidCompleteDate } from "./index";

export const MAX_FILE_SIZE_MB = 1;

export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// TODO: Update the phone number in OVERSIZED_FILES_MESSAGE once the final support contact is confirmed.
export const OVERSIZED_FILES_MESSAGE = 'One or more of your files exceed the max size limit of 1 MB. If you need assistance, please call (xxx) xxx - xxxx.'

export const validatePersonalInfo = (data: Partial<IEmployeeInfo>) => {
  const errors: Record<string, JSX.Element | string> = {};
  if (!data.employeeFirstName?.trim()) {
    errors.employeeFirstName = "Employee First Name is mandatory.";
  }
  if (!data.employeeLastName?.trim()) {
    errors.employeeLastName = "Employee Last Name is mandatory.";
  }
  if (!data.employeePhoneNumber?.trim()) {
    errors.employeePhoneNumber = "Telephone is mandatory.";
  } else if (!/^\d{10}$/.test(data.employeePhoneNumber.replace(/\D/g, ""))) {
    errors.employeePhoneNumber = "Please enter a valid telephone number.";
  }
  if (!data.employeeEmail?.trim()) {
    errors.employeeEmailField = "Employee Email is mandatory.";
  }
  return errors;
};

export const validateLeaveDetails = (data: Partial<IEmployeeInfo>) => {
  const errors: Record<string, JSX.Element | string> = {};
  if (data.WIApproved === null) {
    errors.WIApproved = "Weekly Indemnity field is mandatory.";
  }
  if (data.unpaidMedicalLeaveApproved === null) {
    errors.unpaidMedicalLeaveApproved =
      "Have you been approved for an unpaid Medical Leave field is mandatory.";
  }
  if (!data.reasonForLeave?.trim()) {
    errors.reasonForLeave = "Reason for leave field is mandatory.";
  }
  if (data.regularWorkforce === null) {
    errors.regularWorkforce = "Are you regular workforce field is mandatory.";
  }
  if (data.eligibleToWork === null) {
    errors.eligibleToWork = "Are you eligible to work field is mandatory.";
  }
  if (data.workRestriction === null) {
    errors.workRestriction =
      "Is there a restriction which would prevent the employee from being scheduled or working the day and shift requested for personal leave field is mandatory.";
  }
  if (data.dispatchedToWork === null) {
    errors.dispatchedToWork =
      "Would the employee have been dispatched to work or otherwise scheduled to work based on training and seniority field is mandatory.";
  }
  return errors;
};

export const validateDaysRequested = (
  data: Partial<IEmployeeInfo>,
  blurredFields?: Partial<Record<keyof IEmployeeInfo, boolean>>
) => {
  const errors: Record<string, JSX.Element | string> = {};

  const isFromDateValid = isValidCompleteDate(data.requestedDateFrom);
  const isToDateValid = isValidCompleteDate(data.requestedDateTo);
  const bothDatesComplete = isFromDateValid && isToDateValid;

  const fromDateBlurred = blurredFields?.requestedDateFrom;
  const toDateBlurred = blurredFields?.requestedDateTo;

  // Only show incomplete date errors when dates are actually incomplete AND have been blurred
  if (!bothDatesComplete) {
    if (fromDateBlurred && !isFromDateValid) {
      errors.requestedDateFrom = "Please enter a complete 'From' date.";
    }
    if (toDateBlurred && !isToDateValid) {
      errors.requestedDateTo = "Please enter a complete 'To' date.";
    }
    return errors;
  }

  // Both dates are complete and valid, now proceed with all validation rules
  const fromDate = dayjs(data.requestedDateFrom, DATE_FORMAT);
  const toDate = dayjs(data.requestedDateTo, DATE_FORMAT);
  const numDays = calculateDaysBetweenDates(data.requestedDateFrom, data.requestedDateTo);
  const today = dayjs();
  const deadline = toDate.add(14, "day").endOf("day");
  const oversizedFiles = data.attachments?.filter((file) => file.size > MAX_FILE_SIZE_BYTES);

  // Rule 1: To Date must be after From Date
  if (toDate.diff(fromDate, "day") < 0) {
    errors.requestedDateTo = "The 'To Date' must be after the 'From Date'. Please adjust your requested period of leave accordingly.";
  }
  // Rule 2: Max 5 calendar days allowed
  else if (numDays > 5) {
    errors.requestedDateTo = "The maximum number of days you can request is five (5). Please adjust your requested period of leave.";
  }
  // Rule 3: Must submit within 14 calendar days after To Date
  else if (today.isAfter(deadline)) {
    errors.requestedDateTo = (<>
      <Box sx={{ mb: 1, textAlign: 'left' }}>We regret to inform you that your application for paid personal leave cannot be processed.</Box>
      <Box sx={{ textAlign: 'left' }}>Applications must be received within 14 calendar days following your return to work (based on the day following your entered 'To' date).</Box>
    </>)
  }
  if (oversizedFiles?.length) {
    errors.attachments = OVERSIZED_FILES_MESSAGE;
  }
  return errors;
};

export const validateConfirmation = (data: Partial<IEmployeeInfo>) => {
  const errors: Record<string, JSX.Element | string> = {};
  if (!data?.certifyStatus) {
    errors.certifyStatus = "Confirmation field is mandatory.";
  }
  return errors;
};

/**
 * Validates fields based on the active step
 */
export const validateFields = (
  data: Partial<IEmployeeInfo>,
  step: ProgressBarSteps,
  blurredFields?: Partial<Record<keyof IEmployeeInfo, boolean>>
): Record<string, JSX.Element | string> => {
  switch (step) {
    case ProgressBarSteps.PersonalInfo:
      return validatePersonalInfo(data);
    case ProgressBarSteps.LeaveDetails:
      return validateLeaveDetails(data);
    case ProgressBarSteps.DaysRequested:
      return validateDaysRequested(data, blurredFields);
    case ProgressBarSteps.Confirmation:
      return validateConfirmation(data);
    default:
      return {};
  }
};

/**
 * Calculates the number of days between two date strings
 * Returns 0 if dates are invalid, incomplete, or missing
 */
export const calculateDaysBetweenDates = (fromDate: string | undefined, toDate: string | undefined): number => {
  if (!isValidCompleteDate(fromDate) || !isValidCompleteDate(toDate)) {
    return 0;
  }

  const from = dayjs(fromDate, DATE_FORMAT, true);
  const to = dayjs(toDate, DATE_FORMAT, true);

  const calculatedDays = to.diff(from, "day") + 1;
  return Math.max(calculatedDays, 0);
};
